import { defineStore } from 'pinia';
import { useNuxtApp } from '#app'; // Needed for Nuxt integration
import { useUserStore } from './user';

export const useCardCollectionStore = defineStore('card_collection', {
  state: () => ({
    collection_cards: [],
    loading: false,
    isFavouritesSidebarOpen: false,
  }),
  actions: {
    async fetchCollectionCards(userID) {
      const { $supabase } = useNuxtApp();
      this.loading = true;

      if (userID != '') {
        const { data: collectionData, error: collectionError } = await $supabase
          .from('user_card_collection')
          .select('card_id')
          .eq('user_id', userID);

        if (collectionError) {
          console.error('Error checking card Favourites:', collectionError);
          this.loading = false;
        } else if (collectionData && collectionData.length > 0) {
          const collectionCardIDs = collectionData.map((item) => item.card_id);

          // Fetch full card details for the collection
          if (collectionCardIDs.length > 0) {
            const { data: cardsData, error: cardsError } = await $supabase
              .from('cards')
              .select('*')
              .in('id', collectionCardIDs);

            if (cardsError) {
              console.error('Error fetching card details:', cardsError);
              this.collection_cards = [];
              this.loading = false;
            } else {
              this.collection_cards = cardsData || [];
              this.loading = false;

              return {
                added_at: cardsData?.added_at || new Date().toISOString(),
              };
            }
            this.loading = false;

            this.collection_cards = [];
          }
        } else {
          this.loading = false;
          this.collection_cards = []; // Clear the state if no data
        }
      } else {
        this.loading = false;
      }
    },

    checkIfCardInCollection(cardId) {
      if (this.collection_cards.length > 0) {
        return this.collection_cards.some((card) => card.id === cardId);
      }
      return false;
    },

    async toggleCardInCollection(user, card) {
      this.loading = true;
      const userID = user?.id || '';
      const { public: publicConfig } = useRuntimeConfig();
      const userStore = useUserStore();

      if (userID != '') {
        const { $supabase } = useNuxtApp(); // Access $supabase client
        const isInCollection = this.checkIfCardInCollection(card.id);

        if (isInCollection) {
          // Remove the card from collection
          this.collection_cards = this.collection_cards.filter(
            (c) => c.id !== card.id
          );
          // remove from database

          const { error } = await $supabase
            .from('user_card_collection')
            .delete()
            .eq('card_id', card.id)
            .eq('user_id', userID);

          if (error) {
            console.error('Error removing card from Favourites:', error);
            this.loading = false;
            return {
              success: false,
              message: 'Error removing card from Favourites',
            };
          } else {
            this.loading = false;
            return { success: true, message: 'Card removed from Favourites' };
          }
        } else {
          // Add the card to collection
          // Check collection limit
          const collectionLimit = userStore.isAdmin
            ? publicConfig.collectionLimitAdmin
            : user?.is_commercial_user === true
              ? publicConfig.collectionLimitCommercial
              : publicConfig.collectionLimitCommunity;

          if (this.collection_cards.length >= collectionLimit) {
            this.loading = false;

            if (user?.is_commercial_user === false) {
              return {
                success: false,
                message: `Favourites limit of ${collectionLimit} cards reached. Upgrade your plan to add more.`,
              };
            }
            return {
              success: false,
              message: `Favourites limit of ${collectionLimit} cards reached.`,
            };
          }

          this.collection_cards.push(card);

          const { error } = await $supabase
            .from('user_card_collection')
            .insert({
              user_id: userID,
              card_id: card.id,
              added_at: new Date().toISOString(),
            });

          if (error) {
            console.error('Error adding card to Favourites:', error);
            this.loading = false;
            return {
              success: false,
              message: 'Error adding card to Favourites',
            };
          } else {
            this.loading = false;
            return { success: true, message: 'Card added to Favourites' };
          }
        }
      } else {
        this.loading = false;
        return { success: false, message: 'User not logged in' };
      }
    },

    // Additional helper methods for collection management
    getCollectionCount() {
      return this.collection_cards.length;
    },

    clearCollection() {
      this.collection_cards = [];
    },

    getCollectionCardIds() {
      return this.collection_cards.map((card) => card.card_id);
    },
    openFavouritesSidebar() {
      this.isFavouritesSidebarOpen = true;
    },
    closeFavouritesSidebar() {
      this.isFavouritesSidebarOpen = false;
    },
  },
});
